import { html, type TemplateResult } from 'lit-html'
import type { ShareCard } from '@guild/core'

import type { App } from '../app.ts'
import { chartCard } from '../chart-card.ts'
import { communityLink } from '../community.ts'
import { dayStore } from '../day.ts'
import { defineScreen } from '../screen.ts'
import { focusSlip, practiceSlip, slip } from '../slip.ts'
import type { Store } from '../store.ts'
import { errorText } from '../text.ts'
import { tideLine } from '../tide.ts'

/**
 * Day's end (#58, redrawn by #141).
 *
 * The card is a chart (Q32.1): the island's thumbnail over the sentences, and
 * `[ copy the card ]` copies the text form -- `share.txt`, already rendered in
 * the reader's locale by the server, the single endpoint that answers with
 * words rather than ids, and ASCII plus the chart squares so that it survives
 * being pasted somewhere the Guild's fonts are not (Q7.2, Q32.3). The numbers
 * beside it come from `share.json`, and the thumbnail from the Atlas, which is
 * where the same picture is drawn small.
 */

type State = {
  day: string | null
  card: ShareCard | null
  text: string | null
  thumbnail: string | null
  copied: boolean
  problem: string | null
  /** The practice shore's last word, as a `ui` key, over the card (#134). */
  note: string | null
}

export const dayEndScreen = defineScreen<State>({
  id: 'day',
  initial: {
    day: null,
    card: null,
    text: null,
    thumbnail: null,
    copied: false,
    problem: null,
    note: null,
  },

  async enter(app, route, store, current) {
    store.set({ problem: null, copied: false, note: null })
    let day = route.screen === 'day' ? route.day : null
    if (day === null) {
      const island = await app.api.island()
      if (!current()) return
      if (!island.ok) {
        if (island.status === 401) await app.setSession(null)
        if (current()) store.set({ problem: errorText(app, island) })
        return
      }
      day = island.value.day
    }

    const [card, text, atlas] = await Promise.all([
      app.api.shareCard(day),
      app.api.shareText(day),
      app.api.atlas(),
    ])
    if (!current()) return
    if (!card.ok) {
      store.set({ day, problem: errorText(app, card) })
      return
    }
    // The Atlas is only here for the thumbnail: without it the card is the
    // sentences alone, which is still the card.
    const thumbnail = atlas.ok
      ? (atlas.value.islands.find((island) => island.day === day)?.thumbnail ?? null)
      : null
    if (!text.ok) {
      store.set({ day, card: card.value, thumbnail, problem: errorText(app, text) })
      return
    }
    // The practice shore's last word, once the loop has been walked to its
    // card (#134): the practice itself says when that is.
    const note = practiceSlip(app, 'practiceEnd')
    store.set({ day, card: card.value, text: text.value, thumbnail, note })
    if (note !== null) focusSlip()
  },

  keydown(event, state, _app, store) {
    if (state.note !== null && event.key === 'Escape') {
      event.preventDefault()
      store.set({ note: null })
    }
  },

  render(state, app, store) {
    return html`<section class="screen screen-card">
      <p class="label">${app.t('dayEndHeading')}</p>
      ${state.problem === null ? null : html`<p class="problem" role="alert">${state.problem}</p>`}
      ${
        state.card === null
          ? null
          : html`<h1 class="title">${state.card.islandName}</h1>
              <p class="meta">${state.card.dayLabel}</p>`
      }
      ${note(app, store, state)}
      ${
        state.card === null
          ? null
          : html`<div class="share">
              ${chartCard(app, {
                card: state.card,
                text: state.text,
                thumbnail: state.thumbnail,
                copied: state.copied,
                onCopied: () => store.set({ copied: true }),
              })}
            </div>`
      }
      ${
        // When the tide turns, under the card (#139): today's, whichever
        // day's card this is, and never on the practice shore (#134).
        app.practice === null
          ? tideLine(app, 'tideTurnsAt', dayStore.get().today?.turnsAt ?? null)
          : null
      }
      <div class="row">
        <button class="verb" @click=${() => app.go({ screen: 'post', publicId: null })}>
          [ ${app.t('toThePost')} ]
        </button>
      </div>
      <p class="note">${app.t('dayEndFooter')}</p>
      <!-- The moment a player has something to say (#138). -->
      <p class="row">${communityLink(app)}</p>
    </section>`
  },
})

/**
 * The practice shore's slip over the card (#134): the whole of a day, walked --
 * above it, because the slip says "the card below".
 * The way off the shore is on it, because the real tide is what comes next;
 * `[ noted ]` keeps the player on the card a moment longer.
 */
function note(app: App, store: Store<State>, state: State): TemplateResult | null {
  if (state.note === null) return null
  return slip(app, {
    body: app.t(state.note),
    verbs: [
      { key: 'practiceLeave', primary: true, press: () => void app.leavePractice() },
      { key: 'foundNoted', press: () => store.set({ note: null }) },
    ],
  })
}
